/**
 * 69.4 step 6b, build costs (the step 6 note's (2), BUILDLIST 69.4; DECISIONS 166 (4), the owner: "you can't build certain
 * things without supply (hmg, fob, other stuff probably)"; 171 Q2/Q7 with the note's proposals as the defaults), with
 * GameConfig.buildCosts (needs convoys: the material travels by truck).
 * - **Material** is a generic count of its own (166 (1) keeps the crate and its ammo refills as they are): the base's
 *   (`state.convoys.material`, CONVOY.baseStock loads' worth at the start, a load's more each CONVOY.baseRefillS up to it), a
 *   FOB's store (`supply.material`, up to BUILD_SUPPLY.fobCap) and a truck's cargo (`cargo.material`). A truck loading at
 *   home takes a load's material with its crate refill; at a FOB it hands it over with the crate's (core/convoys.ts).
 * - **The cost** (BUILD_COSTS: relay 10, beacon 20, HMG 40, FOB 100) — for the kinds built on the spot (the FOB, the HMG;
 *   `paidAtPlanting`) — is drawn from **the nearest store of the builder's side within BUILD_SUPPLY.reach** of the build spot that holds enough — a FOB's (linked or cut: it is on the spot) or the
 *   base's (around its HQ, else its landing; only what lies above the minimum saved for the runs, CONVOY.baseMinimum
 *   loads). A FOB is paid by a FOB within reach that can, else by the base wherever it stands (the owner, Oct 13: the
 *   first FOB always stands past FOB.minFromBase, out of the base's reach).
 * - **The carried kinds** (the relay, the beacon) are paid when drawn into the carry instead: the setup's issue from the
 *   base's material (core/convoys.ts `withConvoys`), a restock at a crate from the nearest store (core/buildKits.ts).
 * - **When:** checked before a planting starts (none in reach: it waits, the task kept) and paid as the structure stands; a
 *   store emptied meanwhile stops the planting with the count kept (core/build.ts `stepBuilds`). Nothing is refunded.
 * Pure; nothing here runs without the switch.
 */
import type { BuildKind, GameState, StructureState, Vec3 } from '../types';
import { BUILD_COSTS, BUILD_SUPPLY, CONVOY } from '../constants';
import { convoysOn } from './convoys';
import { structureTeam } from './body';
import { arcDistance } from './gameState';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';

type Spot = { planetIndex: number; surfaceDir: Vec3 };
export type Store = { kind: 'base'; team: number } | { kind: 'fob'; structureId: number };

/** Are build costs on in this match (they need convoys, which carry the material)? */
export function costsOn(config: Pick<GameState['config'], 'buildCosts' | 'convoys' | 'fobs' | 'relays' | 'vehicleStock'>): boolean {
  return !!config.buildCosts && convoysOn(config);
}

/**
 * 69.4 step 6's bot half (c): is this kind paid for where it is planted? The kinds built on the spot are — the FOB and the
 * HMG. The kinds a squad carries (the relay, the beacon) are paid when drawn into the carry — at the setup's issue, from the
 * base, and at a crate's restock (core/buildKits.ts) — and not again at planting: a relay goes where the web does not reach
 * yet, past any store (the probe, scripts/buildStalls.ts: with costs at planting the commander planted none).
 */
export function paidAtPlanting(kind: BuildKind | 'hmg'): boolean {
  return kind === 'fob' || kind === 'hmg';
}

/** What a build of this kind costs in material. */
export function buildCost(kind: BuildKind | 'hmg'): number {
  return BUILD_COSTS[kind];
}

/** Where a side's base store stands: its standing HQ (the lowest id), else its base on the board, else its landing. */
export function baseStoreSpot(state: Pick<GameState, 'structures' | 'players' | 'board' | 'actionHomes' | 'planets'>, team: number): Spot | null {
  const hq = state.structures.filter(s => s.type === 'ecotourism' && s.health > 0 && structureTeam(state, s) === team).sort((a, b) => a.id - b.id)[0];
  if (hq) return { planetIndex: hq.planetIndex, surfaceDir: hq.surfaceDir };
  const base = state.board?.bases.find(b => b.team === team);
  if (base) return { planetIndex: base.planetIndex, surfaceDir: base.dir };
  const home = state.actionHomes?.[team], lead = state.players.find(p => p.teamIndex === team);
  return home && lead ? { planetIndex: lead.planetIndex, surfaceDir: home } : null;
}

/** The base's material a build may spend: what lies above the runs' saved minimum. */
export function baseSpendable(state: Pick<GameState, 'convoys'>, team: number): number {
  return Math.max(0, (state.convoys?.material?.[team] ?? 0) - CONVOY.baseMinimum * CONVOY.material);
}

/**
 * The nearest store of `team` within reach of `at` holding at least `cost`, or null. A FOB (`kind` 'fob'; the owner's
 * choice, Oct 13) is paid by a nearer FOB in reach that can, else by the base wherever it stands — the commander's FOB is
 * the base's decision, and the first one always stands past FOB.minFromBase, out of the base's reach.
 */
export function storeFor(state: GameState, team: number, at: Spot, cost: number, kind?: BuildKind | 'hmg'): Store | null {
  const planet = state.planets[at.planetIndex];
  if (!planet) return null;
  const reach = BUILD_SUPPLY.reach * PERSON_BODY_HEIGHT * personSetOf(state).k;
  let best: Store | null = null, bestD = Infinity;
  const consider = (store: Store, dir: Vec3) => {
    const d = arcDistance(dir, at.surfaceDir, planet.radius);
    if (d <= reach && d < bestD) { best = store; bestD = d; }
  };
  for (const s of [...state.structures].sort((a, b) => a.id - b.id)) {
    if (s.type !== 'fob' || s.health <= 0 || s.planetIndex !== at.planetIndex || (s.supply?.material ?? 0) < cost || structureTeam(state, s) !== team) continue;
    consider({ kind: 'fob', structureId: s.id }, s.surfaceDir);
  }
  const base = baseStoreSpot(state, team);
  if (base && base.planetIndex === at.planetIndex && baseSpendable(state, team) >= cost) consider({ kind: 'base', team }, base.surfaceDir);
  if (!best && kind === 'fob' && baseSpendable(state, team) >= cost) return { kind: 'base', team };   // (a FOB: the base pays wherever it stands)
  return best;
}

/** Why `team` can't pay for `kind` at `at` (null when it can, or with costs off). */
export function costRefusal(state: GameState, team: number, at: Spot, kind: BuildKind | 'hmg'): string | null {
  if (!costsOn(state.config) || !paidAtPlanting(kind)) return null;   // (a carried kind was paid when drawn)
  const cost = buildCost(kind);
  return storeFor(state, team, at, cost, kind) ? null : `No supply within reach: ${cost} needed`;
}

/** The state with `cost` taken from `store` (its structures and the convoys' base material). */
export function spend(state: Pick<GameState, 'structures' | 'convoys'>, store: Store, cost: number): { structures: StructureState[]; convoys: GameState['convoys'] } {
  if (store.kind === 'base') {
    const c = state.convoys!;
    return { structures: state.structures, convoys: { ...c, material: { ...(c.material ?? {}), [store.team]: (c.material?.[store.team] ?? 0) - cost } } };
  }
  return {
    structures: state.structures.map(s => (s.id === store.structureId && s.supply ? { ...s, supply: { ...s.supply, material: (s.supply.material ?? 0) - cost } } : s)),
    convoys: state.convoys,
  };
}
